import useSWRMutation from 'swr/mutation';
import { mutate } from 'swr';
import userCache from '@mentimeter/user';
import {
  mutateWorkspace,
  useInvalidateCurrentUserWorkspace,
} from '@mentimeter/workspace-data-hooks';
import { postConsumeInviteRequest } from '@core-api/workspace-members/workspace-members/consume-invite';
import { captureException } from '@sentry/nextjs';
import { InviteError } from '../../errors/InviteError';
import type { JoinErrorEnum } from '../join-page/handleErrorMessage';

const CONSUME_TOKEN_CACHE_KEY = '/consume-invite';

interface UseConsumeOptions {
  onSuccess: () => void;
  onError: (error: InviteError) => void;
}

export function useConsumeToken(
  token: string,
  { onError, onSuccess }: UseConsumeOptions,
) {
  const invalidateCurrentWorkspace = useInvalidateCurrentUserWorkspace();
  return useSWRMutation(
    CONSUME_TOKEN_CACHE_KEY,
    async () => {
      const request = postConsumeInviteRequest({
        region: userCache.region,
        userAuth: userCache.getToken(),
        inviteToken: token,
      });

      const response = await fetch(request);
      if (!response.ok) {
        const body = (await response.json().catch(() => undefined)) as
          | { code?: JoinErrorEnum }
          | undefined;
        throw new InviteError(body?.code);
      }
    },
    {
      throwOnError: false,
      onSuccess: () => {
        mutate('/users');
        mutateWorkspace();
        invalidateCurrentWorkspace();
        onSuccess();
      },
      onError(e: InviteError) {
        let error = e;
        if (!(error instanceof InviteError)) {
          captureException(error);
          error = new InviteError(undefined);
        }
        onError(error);
      },
    },
  );
}

export function getErrorCode(error: any): JoinErrorEnum {
  return error.response?.data?.code ?? 'error';
}
